/**
 * Image Optimization Utilities
 *
 * Provides utilities for automatic image optimization including:
 * - srcSet generation for S3 images
 * - Progressive fallback handling
 * - Responsive sizes configuration
 *
 * @module imageOptimization
 */

// Configuration
const IMAGE_SIZES = [50, 400, 800, 1280];
const OPTIMIZED_FORMAT = 'avif';

/**
 * Detecta si una URL es optimizable (S3 vs local)
 *
 * @param {string} url - URL de la imagen
 * @returns {boolean} true si es optimizable
 *
 * @example
 * isOptimizableUrl('https://s3.amazonaws.com/image.jpg') // true
 * isOptimizableUrl('/images/local.jpg') // false
 */
export const isOptimizableUrl = (url) => {
  if (!url || typeof url !== 'string') return false;

  // Omitir imágenes locales
  const localPatterns = [
    /^\/images\//,
    /^\.\/images\//,
    /^\.\.\/images\//,
    /^\/assets\//,
    /^blob:/,
    /^data:image\//,
  ];

  if (localPatterns.some((pattern) => pattern.test(url))) {
    return false;
  }

  // Solo URLs absolutas (S3)
  return /^https?:\/\//.test(url);
};

/**
 * Extrae información del archivo desde URL
 *
 * @param {string} url - URL de la imagen
 * @returns {object|null} Objeto con name, extension, basePath, queryParams o null si falla
 *
 * @example
 * extractFileInfo('https://s3.amazonaws.com/images/photo.jpg?v=1')
 * // { name: 'photo', extension: 'jpg', basePath: 'https://s3.amazonaws.com/images', queryParams: '?v=1' }
 */
export const extractFileInfo = (url) => {
  try {
    const urlObj = new URL(url);
    const pathname = urlObj.pathname;
    const filename = pathname.substring(pathname.lastIndexOf('/') + 1);
    const lastDotIndex = filename.lastIndexOf('.');

    if (lastDotIndex === -1) return null;

    return {
      name: filename.substring(0, lastDotIndex),
      extension: filename.substring(lastDotIndex + 1),
      basePath: url.substring(0, url.lastIndexOf('/')),
      queryParams: urlObj.search,
    };
  } catch {
    return null;
  }
};

/**
 * Genera srcSet desde URL S3
 *
 * @param {string} url - URL original
 * @param {array} sizes - Tamaños (default: [50, 400, 800, 1280])
 * @returns {string|null} String srcSet o null si no es optimizable
 *
 * @example
 * generateSrcSet('https://s3.amazonaws.com/images/photo.jpg')
 * // 'https://s3.amazonaws.com/images/photo_file_50.avif 50w, ...'
 */
export const generateSrcSet = (url, sizes = IMAGE_SIZES) => {
  if (!isOptimizableUrl(url)) return null;

  const fileInfo = extractFileInfo(url);
  if (!fileInfo) return null;

  const { name, basePath, queryParams } = fileInfo;

  const srcSetEntries = sizes.map((size) => {
    const optimizedFilename = `${name}_file_${size}.${OPTIMIZED_FORMAT}`;
    const optimizedUrl = `${basePath}/${optimizedFilename}${queryParams}`;
    return `${optimizedUrl} ${size}w`;
  });

  return srcSetEntries.join(', ');
};

/**
 * Genera atributo sizes responsivo
 *
 * @param {array|null} breakpoints - Breakpoints personalizados
 * @returns {string} Atributo sizes
 *
 * @example
 * generateSizes()
 * // '(max-width: 640px) 100vw, (max-width: 768px) 80vw, ...'
 *
 * generateSizes(['(max-width: 768px) 100vw', '50vw'])
 * // '(max-width: 768px) 100vw, 50vw'
 */
export const generateSizes = (breakpoints = null) => {
  const defaultSizes = [
    '(max-width: 640px) 100vw',
    '(max-width: 768px) 80vw',
    '(max-width: 1024px) 60vw',
    '50vw',
  ];

  return breakpoints ? breakpoints.join(', ') : defaultSizes.join(', ');
};

/**
 * Crea handler de fallback progresivo
 *
 * Cuando una imagen optimizada falla, vuelve automáticamente a la original
 *
 * @param {string} originalSrc - Imagen original
 * @param {function} onError - Handler personalizado
 * @returns {function} Handler de error
 *
 * @example
 * const handleError = createFallbackHandler(originalSrc, (e) => console.log('Failed'));
 * <img src={src} srcSet={srcSet} onError={handleError} />
 */
export const createFallbackHandler = (originalSrc, onError) => {
  let fallbackAttempted = false;

  return (event) => {
    if (!fallbackAttempted && event.target) {
      fallbackAttempted = true;

      // Eliminar srcSet para que navegador no reintente variantes
      event.target.removeAttribute('srcset');
      event.target.removeAttribute('sizes');

      // Volver a imagen original
      event.target.src = originalSrc;

      // Llamar handler personalizado si existe
      if (onError) {
        onError(event);
      }
    }
  };
};
